import React, {PureComponent} from 'react'
import PropTypes from 'prop-types'
import withQuery from 'with-query'
import ScrollArea from 'react-scrollarea'
import {GoogleOneTap} from '@rambler-id/google-one-tap'
import {firefox, webView} from '@rambler-id/browser'
import {getRamblerIdHelper} from '@rambler-id/helper'
import {IconButton, Tooltip} from 'rambler-ui'
import * as profileIcons from 'rambler-ui/icons/profiles'
import {collectIdStats, collectUtmParams} from '@rambler-id/url'
import {getProvidersOptions} from '@rambler-id/oauth'
import redirect from 'utils/redirect'
import {LINK_CHAMP} from 'utils/urls'
import {setValidNonce} from 'utils/oauth'
import randomString from 'random-string'
import {allowedProviders} from './providers'
import styles from './styles.css'
import {OdnoklassnikiIcon} from 'components/icons/OdnoklassnikiIcon'

const providersOptions = getProvidersOptions(allowedProviders)

export default class OauthButtons extends PureComponent {
  static propTypes = {
    filter: PropTypes.func,
    scrollable: PropTypes.bool,
    action: PropTypes.string.isRequired,
    cerberId: PropTypes.func,
    className: PropTypes.string
  }

  static defaultProps = {
    scrollable: true
  }

  googleOneTap = new GoogleOneTap({
    clientId: '974710940776.apps.googleusercontent.com'
  })

  constructor(props) {
    super(props)

    const minWidth = this.calculateMinWidth(props)

    this.state = {
      minWidth,
      scroll: window.innerWidth < minWidth
    }
  }

  async componentDidMount() {
    if (this.props.scrollable) {
      window.addEventListener('resize', this.resizeScrollArea, false)
    }

    try {
      await this.googleOneTap.initialize()
    } catch (error) {
      delete this.googleOneTap
    }
  }

  componentWillUnmount() {
    if (this.props.scrollable) {
      window.removeEventListener('resize', this.resizeScrollArea, false)
    }
  }

  calculateMinWidth() {
    return allowedProviders.length * 48 + 22
  }

  async callOauthPopup(action, provider) {
    const {isPrimaryMailBlocked, showBlockedEmailSnackbar} = this.props

    if (isPrimaryMailBlocked) {
      showBlockedEmailSnackbar()

      return
    }

    if (provider === 'championat') {
      redirect(withQuery(LINK_CHAMP, {back: window.location.href}))

      return
    }

    let targetWindow
    let providerParams = {provider}

    const ramblerIdHelper = await getRamblerIdHelper()

    /**
     * NOTE: disable google one tap for firefox browser
     * because created targetWindow
     * is not in focus after one tap click or is opened in new tab as blank page
     */
    if (provider === 'google' && !firefox) {
      if (!webView) {
        targetWindow = ramblerIdHelper.createPropagationWindow()
      }

      try {
        const {credential: code} = await this.googleOneTap.prompt()
        const nonce = randomString({length: 7, special: false}).toLowerCase()

        setValidNonce(nonce)

        providerParams = {provider: 'google-standalone', code, nonce}

        if (targetWindow) {
          targetWindow.close()
        }
      } catch (error) {
        if (error.reason === 'Google One Tap was skipped by user') {
          return
        }
      }
    }

    ramblerIdHelper.openProviderOAuth({
      ...providerParams,
      action,
      popup: true,
      targetWindow,
      ...(action === 'login' && {
        ...collectUtmParams(window.location.href),
        ...collectIdStats(window.location.href)
      })
    })
  }

  resizeScrollArea = () => {
    clearTimeout(this.resizeTimeout)

    this.resizeTimeout = setTimeout(() => {
      const minWidth = this.calculateMinWidth(this.props)

      this.setState({
        minWidth,
        scroll: window.innerWidth < minWidth
      })
    }, 200)
  }

  renderButtons() {
    const {action, scrollable, cerberId = () => undefined, filter} = this.props
    const attach = action === 'attach'

    return allowedProviders.filter(filter).map((name) => {
      const iconName = `${name.replace(/^\w/, (m) => m.toUpperCase())}Icon`
      const ProfileIcon = {...profileIcons, OdnoklassnikiIcon}[iconName]
      const withoutTooltip = scrollable && this.state.scroll

      const button = (
        <IconButton
          {...(withoutTooltip && {
            key: name,
            className: styles.button
          })}
          size={attach ? 45 : 40}
          type={attach ? 'secondary' : 'outline'}
          aria-label={providersOptions[name].title}
          onClick={(event) => this.callOauthPopup(action, name, event)}
          data-cerber-id={cerberId(name)}>
          <ProfileIcon size={18} />
        </IconButton>
      )

      if (withoutTooltip) {
        return button
      }

      return (
        <Tooltip
          className={styles.button}
          key={name}
          content={providersOptions[name].title}>
          {button}
        </Tooltip>
      )
    })
  }

  renderArea() {
    const buttons = this.renderButtons()

    if (this.props.scrollable && this.state.scroll) {
      return (
        <ScrollArea
          vertical={false}
          focusableTabIndex={0}
          contentStyle={{minWidth: this.state.minWidth}}
          horizontalScrollbarStyle={{display: 'none'}}>
          {buttons}
        </ScrollArea>
      )
    }

    return buttons
  }

  render() {
    return (
      <article className={`${styles.normal} ${this.props.className}`}>
        {this.renderArea()}
      </article>
    )
  }
}
